import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

// ── Types matching w-cms /api/v1 responses ──

export interface ContentCategory {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  name: string;
  slug: string;
  description: string;
  icon_url: string;
  parent_id?: number;
  sort_order: number;
  is_active: boolean;
}

export interface HealthTip {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  title: string;
  content: string;
  category: string;
  category_id?: number;
  icon_url: string;
  source: string;
  tags: string;
  is_premium: boolean;
  is_active: boolean;
  sort_order: number;
  view_count: number;
}

export interface DietChart {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  title: string;
  description: string;
  diet_type: string;
  meal_type: string;
  calorie_range_min: number;
  calorie_range_max: number;
  age_group: string;
  items: string;
  instructions: string;
  nutrients: string;
  duration: string;
  thumbnail_url: string;
  category_id?: number;
  tags: string;
  author: string;
  is_premium: boolean;
  is_active: boolean;
  sort_order: number;
  view_count: number;
}

export interface ExerciseContent {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  title: string;
  description: string;
  exercise_type: string;
  difficulty: string;
  duration_minutes: number;
  calories_burned: number;
  equipment_needed: string;
  instruction_steps: string;
  precautions: string;
  benefits_for: string;
  video_url: string;
  thumbnail_url: string;
  category_id?: number;
  tags: string;
  author: string;
  is_premium: boolean;
  is_active: boolean;
  sort_order: number;
  view_count: number;
}

export interface VideoContent {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  title: string;
  description: string;
  video_url: string;
  thumbnail_url: string;
  duration_secs: number;
  category: string;
  category_id?: number;
  tags: string;
  author: string;
  language: string;
  is_premium: boolean;
  is_active: boolean;
  sort_order: number;
  view_count: number;
}

export interface SuggestedTest {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  test_name: string;
  test_code: string;
  reason: string;
  frequency: string;
  age_group_min: number;
  age_group_max: number;
  gender: string;
  conditions: string;
  priority: number;
  category_id?: number;
  is_premium: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface ContentState {
  categories: ContentCategory[];
  healthTips: HealthTip[];
  dietCharts: DietChart[];
  exercises: ExerciseContent[];
  videos: VideoContent[];
  suggestedTests: SuggestedTest[];
  loading: boolean;
  error: string | null;
}

const initialState: ContentState = {
  categories: [],
  healthTips: [],
  dietCharts: [],
  exercises: [],
  videos: [],
  suggestedTests: [],
  loading: false,
  error: null,
};

const unwrap = (p: any) => (Array.isArray(p?.data) ? p.data : Array.isArray(p) ? p : []);
const unwrapOne = (p: any) => p?.data || p || null;

// ── Generic factory to reduce duplication ──
function makeCrudThunks<T extends { ID: number }>(
  resource: string,            // e.g. 'content/healthTips'
  endpoint: string,            // e.g. 'health-tips'
) {
  const base = `${config.services.cms}/api/v1/${endpoint}`;
  return {
    fetch: createAsyncThunk(`${resource}/fetch`, async (_: void, thunkAPI) => {
      try {
        const r = await apiClient.get(base);
        return r.data;
      } catch (e: any) { return thunkAPI.rejectWithValue(e.message || 'Failed'); }
    }),
    create: createAsyncThunk(`${resource}/create`, async (data: Partial<T>, thunkAPI) => {
      try {
        const r = await apiClient.post(base, data);
        return r.data;
      } catch (e: any) { return thunkAPI.rejectWithValue(e.message || 'Failed'); }
    }),
    update: createAsyncThunk(`${resource}/update`, async ({ id, data }: { id: number; data: Partial<T> }, thunkAPI) => {
      try {
        const r = await apiClient.put(`${base}/${id}`, data);
        return r.data;
      } catch (e: any) { return thunkAPI.rejectWithValue(e.message || 'Failed'); }
    }),
    remove: createAsyncThunk(`${resource}/delete`, async (id: number, thunkAPI) => {
      try {
        await apiClient.delete(`${base}/${id}`);
        return id;
      } catch (e: any) { return thunkAPI.rejectWithValue(e.message || 'Failed'); }
    }),
  };
}

export const categoriesThunks = makeCrudThunks<ContentCategory>('content/categories', 'content-categories');
export const healthTipsThunks = makeCrudThunks<HealthTip>('content/healthTips', 'health-tips');
export const dietChartsThunks = makeCrudThunks<DietChart>('content/dietCharts', 'diet-charts');
export const exercisesThunks = makeCrudThunks<ExerciseContent>('content/exercises', 'exercises');
export const videosThunks = makeCrudThunks<VideoContent>('content/videos', 'videos');
export const suggestedTestsThunks = makeCrudThunks<SuggestedTest>('content/suggestedTests', 'suggested-tests');

// ── Re-exports for convenience ──
export const fetchCategories = categoriesThunks.fetch;
export const createCategory = categoriesThunks.create;
export const updateCategory = categoriesThunks.update;
export const deleteCategory = categoriesThunks.remove;

export const fetchHealthTips = healthTipsThunks.fetch;
export const createHealthTip = healthTipsThunks.create;
export const updateHealthTip = healthTipsThunks.update;
export const deleteHealthTip = healthTipsThunks.remove;

export const fetchDietCharts = dietChartsThunks.fetch;
export const createDietChart = dietChartsThunks.create;
export const updateDietChart = dietChartsThunks.update;
export const deleteDietChart = dietChartsThunks.remove;

export const fetchExercises = exercisesThunks.fetch;
export const createExercise = exercisesThunks.create;
export const updateExercise = exercisesThunks.update;
export const deleteExercise = exercisesThunks.remove;

export const fetchVideos = videosThunks.fetch;
export const createVideo = videosThunks.create;
export const updateVideo = videosThunks.update;
export const deleteVideo = videosThunks.remove;

export const fetchSuggestedTests = suggestedTestsThunks.fetch;
export const createSuggestedTest = suggestedTestsThunks.create;
export const updateSuggestedTest = suggestedTestsThunks.update;
export const deleteSuggestedTest = suggestedTestsThunks.remove;

function applyCrudReducers<K extends keyof Omit<ContentState, 'loading' | 'error'>>(
  builder: any,
  thunks: ReturnType<typeof makeCrudThunks<any>>,
  key: K,
) {
  builder
    .addCase(thunks.fetch.pending, (state: ContentState) => { state.loading = true; state.error = null; })
    .addCase(thunks.fetch.fulfilled, (state: ContentState, action: any) => {
      state.loading = false;
      (state[key] as any) = unwrap(action.payload);
    })
    .addCase(thunks.fetch.rejected, (state: ContentState, action: any) => {
      state.loading = false; state.error = action.payload as string;
    })
    .addCase(thunks.create.fulfilled, (state: ContentState, action: any) => {
      const item = unwrapOne(action.payload);
      if (item) (state[key] as any).push(item);
    })
    .addCase(thunks.update.fulfilled, (state: ContentState, action: any) => {
      const updated = unwrapOne(action.payload);
      if (updated) {
        const list = state[key] as any[];
        const idx = list.findIndex(x => x.ID === updated.ID);
        if (idx !== -1) list[idx] = updated;
      }
    })
    .addCase(thunks.remove.fulfilled, (state: ContentState, action: any) => {
      const id = action.payload;
      (state[key] as any) = (state[key] as any[]).filter(x => x.ID !== id);
    });
}

const contentSlice = createSlice({
  name: 'content',
  initialState,
  reducers: {
    clearContentError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    applyCrudReducers(builder, categoriesThunks, 'categories');
    applyCrudReducers(builder, healthTipsThunks, 'healthTips');
    applyCrudReducers(builder, dietChartsThunks, 'dietCharts');
    applyCrudReducers(builder, exercisesThunks, 'exercises');
    applyCrudReducers(builder, videosThunks, 'videos');
    applyCrudReducers(builder, suggestedTestsThunks, 'suggestedTests');
  },
});

export const { clearContentError } = contentSlice.actions;
export default contentSlice.reducer;
